import React from "react"
import PropTypes from "prop-types"
import Icon from '../app_components/Icon'
import ProfileLanguageFlag from '../app_components/ProfileLanguageFlag'
import { semantic } from '@lexody/design-tokens'
import {
  resolveAvatarUrl,
  fallbackAvatarOnError,
  formatLocation,
  getNativeLanguages,
  getActivityBadgeLabel,
  getLanguageBadgeEntries,
  truncateText,
  formatActivityLabel,
} from './connectMemberHelpers'

function NativeLanguageFlags({ languagesUsers, className }) {
  const nativeLanguages = getNativeLanguages(languagesUsers)

  if (nativeLanguages.length === 0) return null

  return (
    <div className={className}>
      {nativeLanguages.map((language, index) => (
        <ProfileLanguageFlag
          key={`${language.flag || language.language || language.locale}-${index}`}
          flagHtml={language.flag_html}
        />
      ))}
    </div>
  )
}

function LocationRow({ location }) {
  const formatted = formatLocation(location)
  if (!formatted) return null

  return (
    <p className="connectMemberCard__location">
      <Icon name="map-marker" size={12} color={semantic.greyscale.text.subtitle} />
      <span>{formatted}</span>
    </p>
  )
}

const MAX_LANGUAGE_BADGES = 2

function LanguageBadge({ entry }) {
  if (!entry.flagHtml) return null

  return (
    <span className={`connectMemberCard__languageBadge connectMemberCard__languageBadge--level${entry.level}`}>
      <ProfileLanguageFlag flagHtml={entry.flagHtml} size={14} />
      <span className="connectMemberCard__languageBadgeText">{entry.label}</span>
    </span>
  )
}

class ConnectMemberCard extends React.Component {
  profileUrl() {
    return `/profile/${this.props.user.slug}`
  }

  renderGridCard() {
    const { user, missingImg, premiumUser } = this.props
    const activityLabel = premiumUser ? formatActivityLabel(getActivityBadgeLabel(user.last_seen)) : null
    const location = formatLocation(user.location)
    const languageBadges = getLanguageBadgeEntries(user.languages_users)
    const visibleBadges = languageBadges.slice(0, MAX_LANGUAGE_BADGES)
    const overflowCount = languageBadges.length - MAX_LANGUAGE_BADGES

    return (
      <a
        href={this.profileUrl()}
        className="friendMatchCard friendMatchCard--compact friendMatchCard--clickable"
        aria-label={`View ${user.preferred_name}'s profile`}
      >
        <div className="friendMatchCard__photoWrap">
          <img
            className="friendMatchCard__photo"
            src={resolveAvatarUrl(user.avatar_url, missingImg)}
            alt={`${user.preferred_name}'s profile photo`}
            onError={(e) => fallbackAvatarOnError(e, missingImg)}
          />

          {activityLabel && (
            <div className="friendMatchCard__lastSeen profile-hero__lastSeen profile-hero__lastSeen--active">
              <span className="profile-hero__lastSeenDot" aria-hidden="true" />
              <span>{activityLabel}</span>
            </div>
          )}

          <svg
            className="friendMatchCard__wave"
            viewBox="0 0 320 28"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,18 C50,30 110,6 160,20 C210,34 270,8 320,18 L320,28 L0,28 Z"
              fill="var(--lex-grey-surface-default, #F3F7F9)"
            />
          </svg>
        </div>

        <div className="friendMatchCard__body">
          <p className="friendMatchCard__name">{user.preferred_name}</p>

          {location && (
            <p className="friendMatchCard__location">
              <Icon name="map-marker" size={11} color={semantic.greyscale.text.subtitle} />
              <span>{location}</span>
            </p>
          )}

          {visibleBadges.length > 0 && (
            <div className="connectMemberCard__languageBadges friendMatchCard__languageBadges">
              {visibleBadges.map((entry) => (
                <LanguageBadge key={entry.key} entry={entry} />
              ))}
              {overflowCount > 0 && (
                <span className="connectMemberCard__languageOverflow">
                  +{overflowCount}
                </span>
              )}
            </div>
          )}

          <div className="friendMatchCard__bodyWave" aria-hidden="true" />
        </div>
      </a>
    )
  }

  renderMissedCard() {
    const { user, missingImg, connectMessage } = this.props
    const message = truncateText(connectMessage, 140)
    const profileUrl = this.profileUrl()

    return (
      <article className="connectMemberCard connectMemberCard--missed">
        <div className="connectMemberCard__missedTop">
          <a
            href={profileUrl}
            className="connectMemberCard__missedMain"
          >
            <img
              className="connectMemberCard__missedAvatar"
              src={resolveAvatarUrl(user.avatar_url, missingImg)}
              alt={`${user.preferred_name}'s profile photo`}
              onError={(e) => fallbackAvatarOnError(e, missingImg)}
            />
            <div className="connectMemberCard__info">
              <p className="connectMemberCard__name">{user.preferred_name}</p>
              <NativeLanguageFlags
                languagesUsers={user.languages_users}
                className="connectMemberCard__nativeFlags connectMemberCard__nativeFlags--inline"
              />
              <LocationRow location={user.location} />
            </div>
          </a>
          <a
            href={profileUrl}
            className="connectMemberCard__missedAction"
            aria-label={`View ${user.preferred_name}'s profile`}
          >
            <Icon name="user-plus" size={18} color={semantic.primary.surface.default} />
          </a>
        </div>
        {message && (
          <p className="connectMemberCard__missedMessage">{message}</p>
        )}
      </article>
    )
  }

  render() {
    switch (this.props.variant) {
      case 'missed':
        return this.renderMissedCard()
      case 'grid':
      default:
        return this.renderGridCard()
    }
  }
}

ConnectMemberCard.propTypes = {
  user: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    avatar_url: PropTypes.string,
    preferred_name: PropTypes.string,
    location: PropTypes.string,
    last_seen: PropTypes.string,
    bio: PropTypes.string,
    languages_users: PropTypes.array,
  }).isRequired,
  missingImg: PropTypes.string.isRequired,
  connectMessage: PropTypes.string,
  variant: PropTypes.oneOf(['grid', 'missed']),
  forceOnline: PropTypes.bool,
  premiumUser: PropTypes.bool,
}

ConnectMemberCard.defaultProps = {
  variant: 'grid',
  forceOnline: false,
  premiumUser: false,
}

export default ConnectMemberCard
